import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'
import uPlot from 'uplot'

export interface CursorDataPoint {
  sensorId: string
  yValue: number | null
  xValue: number | string | null
  unit?: string
}

interface CursorDataContextType {
  cursorData: Record<string, CursorDataPoint>
  setCursorData: React.Dispatch<
    React.SetStateAction<Record<string, CursorDataPoint>>
  >
  updateCursorData: (
    sensorId: string,
    yValue: number | null,
    xValue: number | null,
    unit?: string
  ) => void
  clearCursorData: () => void
  getXValueAtCursor: (
    e: React.MouseEvent,
    uPlotInstance: uPlot | null
  ) => number | null
}

const CursorDataContext = createContext<CursorDataContextType | undefined>(
  undefined
)

interface CursorDataProviderProps {
  children: ReactNode
}

export const CursorDataProvider: React.FC<CursorDataProviderProps> = ({
  children,
}) => {
  const [cursorData, setCursorData] = useState<Record<string, CursorDataPoint>>(
    {}
  )

  const updateCursorData = useCallback(
    (
      sensorId: string,
      yValue: number | null,
      xValue: number | null,
      unit?: string
    ) => {
      setCursorData((prev) => ({
        ...prev,
        [sensorId]: { sensorId, yValue, xValue, unit },
      }))
    },
    []
  )

  const clearCursorData = useCallback(() => {
    setCursorData({})
  }, [])

  const getXValueAtCursor = useCallback(
    (e: React.MouseEvent, uPlotInstance: uPlot | null): number | null => {
      if (!uPlotInstance) return null

      const overRect = uPlotInstance.over.getBoundingClientRect()
      const relativeX = e.clientX - overRect.left
      return uPlotInstance.posToVal(relativeX, 'x')
    },
    []
  )

  return (
    <CursorDataContext.Provider
      value={{
        cursorData,
        setCursorData,
        updateCursorData,
        clearCursorData,
        getXValueAtCursor,
      }}>
      {children}
    </CursorDataContext.Provider>
  )
}

export const useCursorData = (): CursorDataContextType => {
  const context = useContext(CursorDataContext)
  if (context === undefined) {
    throw new Error('useCursorData must be used within a CursorDataProvider')
  }
  return context
}
